import type { Typed } from 'services/api/fields/core';

export enum ClassificationApiName {
  EPISODE = 'episode',
  PLAN_TYPE = 'plan_type',
  HEALTH_PLAN = 'health_plan',
}

export enum ClassificationClass {
  EPISODE = 'Classification::Episode',
  PLAN_TYPE = 'Classification::PlanType',
  HEALTH_PLAN = 'Classification::HealthPlan',
}

export interface ClassificationOptions {
  type: string;
  childClassifications?: ClassificationOptions[];
  parentClassification?: ClassificationOptions | null;
  [key: string]: any;
}

function getDefaults(): ClassificationOptions {
  return {
    id: '',
    name: '',
    type: ClassificationClass.PLAN_TYPE,
    clientIds: [],
    clientNames: '',
    childClassifications: [],
  };
}

// Forward declare so _Classification's constructor can return the infused type
// eslint-disable-next-line prefer-const
let Classification: new <T extends ClassificationOptions>(options: T) => Typed<_Classification<T>, T>;

class _Classification<T extends ClassificationOptions = ClassificationOptions> {
  type!: string;
  childClassifications!: Classification<NonNullable<T['childClassifications']>[number]>[];
  parentClassification!: Classification<NonNullable<T['parentClassification']>> | null;

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.type = classificationType(opts.type)!;
    this.childClassifications = (opts.childClassifications ?? []).map(
      (c) => new Classification<NonNullable<T['childClassifications']>[number]>(c)
    );
    this.parentClassification = opts.parentClassification
      ? new Classification<NonNullable<T['parentClassification']>>(opts.parentClassification)
      : null;
  }

  get isHealthPlan() {
    return this.type === ClassificationApiName.HEALTH_PLAN;
  }

  get isPlanType() {
    return this.type === ClassificationApiName.PLAN_TYPE;
  }

  get isEpisode() {
    return this.type === ClassificationApiName.EPISODE;
  }

  get classificationClass() {
    return classificationClass(this.type) ?? '';
  }
}

Classification = _Classification as typeof Classification;

export default Classification;
export { Classification };
export type Classification<T extends ClassificationOptions = ClassificationOptions> = _Classification<T> &
  Omit<T, keyof _Classification<T>>;

export const classificationType = (type: string) => {
  switch (type) {
    case ClassificationClass.PLAN_TYPE:
    case ClassificationApiName.PLAN_TYPE:
      return ClassificationApiName.PLAN_TYPE;
    case ClassificationClass.EPISODE:
    case ClassificationApiName.EPISODE:
      return ClassificationApiName.EPISODE;
    case ClassificationClass.HEALTH_PLAN:
    case ClassificationApiName.HEALTH_PLAN:
      return ClassificationApiName.HEALTH_PLAN;
  }
};

const classificationClass = (type: string) => {
  switch (type) {
    case ClassificationApiName.PLAN_TYPE:
    case ClassificationClass.PLAN_TYPE:
      return ClassificationClass.PLAN_TYPE;
    case ClassificationApiName.EPISODE:
    case ClassificationClass.EPISODE:
      return ClassificationClass.EPISODE;
    case ClassificationApiName.HEALTH_PLAN:
    case ClassificationClass.HEALTH_PLAN:
      return ClassificationClass.HEALTH_PLAN;
  }
};
